import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: {
      organization: [],
      users: [],
      financial: [],
      reports: [],
      settings: []
    }
  });

  const permissionModules = [
    {
      id: 'organization',
      name: 'Organization',
      description: 'Organization profile, branding, and locations',
      actions: [
        { id: 'read', name: 'View', description: 'View organization information' },
        { id: 'write', name: 'Edit', description: 'Modify organization settings' },
        { id: 'delete', name: 'Delete', description: 'Delete organization data' }
      ]
    },
    {
      id: 'users',
      name: 'User Management',
      description: 'User accounts, roles, and permissions',
      actions: [
        { id: 'read', name: 'View', description: 'View user accounts' },
        { id: 'write', name: 'Edit', description: 'Create and modify users' },
        { id: 'delete', name: 'Delete', description: 'Delete user accounts' }
      ]
    },
    {
      id: 'financial',
      name: 'Financial Data',
      description: 'Transactions, invoices, and financial records',
      actions: [
        { id: 'read', name: 'View', description: 'View financial data' },
        { id: 'write', name: 'Edit', description: 'Create and modify financial records' },
        { id: 'delete', name: 'Delete', description: 'Delete financial records' }
      ]
    },
    {
      id: 'reports',
      name: 'Reports & Analytics',
      description: 'Financial reports and business analytics',
      actions: [
        { id: 'read', name: 'View', description: 'View reports' },
        { id: 'write', name: 'Create', description: 'Create custom reports' },
        { id: 'export', name: 'Export', description: 'Export reports and data' }
      ]
    },
    {
      id: 'settings',
      name: 'System Settings',
      description: 'System configuration and preferences',
      actions: [
        { id: 'read', name: 'View', description: 'View system settings' },
        { id: 'write', name: 'Configure', description: 'Modify system settings' }
      ]
    }
  ];

  // TODO: Load roles from database
  React.useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoading(true);
        // TODO: Replace with actual API call to load roles
        // const data = await roleService.getAll();
        // setRoles(data);
        setRoles([]);
      } catch (err) {
        setError(err?.message || 'Failed to load roles');
      } finally {
        setLoading(false);
      }
    };

    loadRoles();
  }, []);

  const handleCreateRole = () => {
    const role = {
      ...newRole,
      id: Math.max(...(roles?.map(r => r?.id) || [0]), 0) + 1,
      userCount: 0,
      isBuiltIn: false
    };
    setRoles(prev => [...prev, role]);
    resetForm();
  };

  const handleUpdateRole = () => {
    setRoles(prev => 
      prev?.map(role => 
        role?.id === editingRole?.id ? { ...editingRole, ...newRole } : role
      )
    );
    resetForm();
  };

  const handleDeleteRole = (roleId) => {
    setRoles(prev => prev?.filter(role => role?.id !== roleId));
  };

  const resetForm = () => {
    setNewRole({
      name: '',
      description: '',
      permissions: {
        organization: [],
        users: [],
        financial: [],
        reports: [],
        settings: []
      }
    });
    setEditingRole(null);
    setShowCreateModal(false);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setNewRole({
      name: role?.name,
      description: role?.description,
      permissions: { ...role?.permissions }
    });
    setShowCreateModal(true);
  };

  const togglePermission = (moduleId, actionId) => {
    setNewRole(prev => ({
      ...prev,
      permissions: {
        ...prev?.permissions,
        [moduleId]: prev?.permissions?.[moduleId]?.includes(actionId)
          ? prev?.permissions?.[moduleId]?.filter(p => p !== actionId)
          : [...(prev?.permissions?.[moduleId] || []), actionId]
      }
    }));
  };

  const duplicateRole = (role) => {
    const duplicatedRole = {
      ...role,
      id: Math.max(...(roles?.map(r => r?.id) || [0]), 0) + 1,
      name: `${role?.name} (Copy)`,
      userCount: 0,
      isBuiltIn: false
    };
    setRoles(prev => [...prev, duplicatedRole]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Icon name="Loader2" size={24} className="animate-spin mr-2" />
        <span>Loading roles...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <Icon name="AlertCircle" size={16} className="text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Roles Management</h2>
          <p className="text-muted-foreground">Manage user roles and configure granular permissions for your organization</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} iconName="Plus">
          Create Role
        </Button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {roles?.length === 0 ? (
          <div className="col-span-full bg-surface border border-border rounded-lg p-8 text-center">
            <div className="flex flex-col items-center space-y-3">
              <Icon name="Shield" size={48} className="text-muted-foreground/50" />
              <h3 className="text-lg font-medium text-foreground">No roles found</h3>
              <p className="text-muted-foreground">Start by creating your first user role with custom permissions</p>
              <Button onClick={() => setShowCreateModal(true)} iconName="Plus">
                Create Role
              </Button>
            </div>
          </div>
        ) : (
          roles?.map((role) => (
            <div key={role?.id} className="bg-surface border border-border rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-medium text-foreground">{role?.name}</h3>
                    {role?.isBuiltIn && (
                      <span className="px-2 py-1 text-xs font-medium bg-muted text-foreground rounded-full">
                        Built-in
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{role?.description}</p>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="flex items-center space-x-1">
                      <Icon name="Users" size={14} />
                      <span>{role?.userCount || 0} users</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Shield" size={14} />
                      <span>
                        {Object.values(role?.permissions || {})?.flat()?.length} permissions
                      </span>
                    </span>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditRole(role)}
                    iconName="Edit"
                  >
                    Edit
                  </Button>
                  {!role?.isBuiltIn && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteRole(role?.id)}
                      iconName="Trash2"
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => duplicateRole(role)}
                    iconName="Copy"
                  >
                    Duplicate
                  </Button>
                </div>
              </div>

              {/* Permissions Summary */}
              <div className="space-y-3">
                {permissionModules?.map((module) => {
                  const modulePermissions = role?.permissions?.[module?.id] || [];
                  return (
                    <div key={module?.id} className="flex items-center justify-between py-2 border-t border-border first:border-t-0">
                      <span className="text-sm font-medium text-foreground">{module?.name}</span>
                      <div className="flex space-x-1">
                        {module?.actions?.map((action) => (
                          <span
                            key={action?.id}
                            className={`px-2 py-1 text-xs rounded ${
                              modulePermissions?.includes(action?.id)
                                ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {action?.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Role Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {editingRole ? 'Edit Role' : 'Create New Role'}
              </h3>
              <Button variant="ghost" size="icon" onClick={resetForm}>
                <Icon name="X" size={16} />
              </Button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Role Name"
                  value={newRole?.name}
                  onChange={(e) => setNewRole(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="Enter role name"
                  required
                />
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Description
                  </label>
                  <textarea
                    value={newRole?.description}
                    onChange={(e) => setNewRole(prev => ({ ...prev, description: e?.target?.value }))}
                    placeholder="Enter role description"
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              </div>

              {/* Permissions */}
              <div>
                <h4 className="text-md font-medium text-foreground mb-4">Permissions</h4>
                <div className="space-y-6">
                  {permissionModules?.map((module) => (
                    <div key={module?.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h5 className="font-medium text-foreground">{module?.name}</h5>
                          <p className="text-sm text-muted-foreground">{module?.description}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {module?.actions?.map((action) => (
                          <label key={action?.id} className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newRole?.permissions?.[module?.id]?.includes(action?.id) || false}
                              onChange={() => togglePermission(module?.id, action?.id)}
                              className="mt-1 h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            />
                            <div>
                              <span className="text-sm font-medium text-foreground">{action?.name}</span>
                              <p className="text-xs text-muted-foreground">{action?.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 p-6 border-t border-border">
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button 
                onClick={editingRole ? handleUpdateRole : handleCreateRole}
                iconName="Save"
              >
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesManagement;